import type { ActionTree } from 'vuex';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/types/enums/recipient-status';
import type { NodeTrackingPayload } from '@getaccept/lib-shared/src/tracking/types/node-tracking-payload';
import type { DocumentRecipientRevision } from '@getaccept/lib-shared/src/types/document-revision';
import { TrackingHelper } from '../helpers/tracking.helper';
import type { RootState } from '../../store/root.types';
import TrackingService from '../../api/tracking/services/tracking.service';
import { RevisionHelper } from '../../helpers/revision.helper';
import type { TrackingState } from './tracking.types';
import { TrackingAction, TrackingMutation } from './tracking.constants';

export const actions: ActionTree<TrackingState, RootState> = {
  [TrackingAction.SetCurrentDocumentPages]({ commit }, pages: DocumentPage[]) {
    commit(TrackingMutation.SetCurrentDocumentPages, pages);
  },
  async [TrackingAction.TrackFieldClick]({ state }, id: string) {
    if (state.trackingDisabled) {
      return;
    }
    try {
      await TrackingService.trackFieldClick(id);
    } catch (_) {
      console.log(`Failed to track click for field with id ${id}`);
    }
  },
  async [TrackingAction.TrackVisitTime](
    { state, rootState, dispatch },
    { pages, pageTime }: { pages: DocumentPage[]; pageTime: number }
  ) {
    const minValueAllowed = 100;

    if (state.trackingDisabled || state.suspendPageTracking || pageTime < minValueAllowed) {
      return;
    }
    try {
      if (!pages) {
        return;
      }
      const revision: DocumentRecipientRevision = RevisionHelper.getLatestSealedRevision(
        rootState['document'].document?.documentRevisions
      );
      pages.forEach(async (page: DocumentPage) => {
        try {
          await TrackingService.trackVisitTime(page.pageNum, pageTime, page.id, revision?.revision);
        } catch (e) {
          console.log(e);
        }
      });
      const sendPush: boolean = TrackingHelper.shouldSendViewedPush(
        rootState['document'].document.recipients,
        rootState.recipientId
      );
      if (sendPush) {
        dispatch('document/setRecipientStatus', { status: RecipientStatus.Viewed }, { root: true });
      }
    } catch (_) {
      console.log(`Failed to track visit time for pages`);
    }
  },
  async [TrackingAction.TrackContentLinkClick]({ state }, id: string) {
    if (state.trackingDisabled) {
      return;
    }
    try {
      await TrackingService.trackContentLinkBlockClick(id);
    } catch (_) {
      console.log(`Failed to track click for content-link with id ${id}`);
    }
  },
  async [TrackingAction.TrackEditorNodeClick]({ state }, nodeTracking: NodeTrackingPayload) {
    if (state.trackingDisabled) {
      return;
    }
    try {
      await TrackingService.trackEditor([], [nodeTracking]);
    } catch (_) {
      console.log(`Failed to track click for node with id ${nodeTracking.nodeId}`);
    }
  },
  async [TrackingAction.TrackVideo](
    { state },
    { id, viewDuration }: { id: string; viewDuration: number }
  ) {
    if (state.trackingDisabled) {
      return;
    }
    try {
      await TrackingService.trackVideo(id, viewDuration);
    } catch (_) {
      console.log(`Failed to track video with id ${id}`);
    }
  },
  [TrackingAction.StartPageViewStopWatch]({ commit, state }) {
    if (state.trackingDisabled || state.suspendPageTracking || !state.currentDocumentPages) {
      return;
    }
    try {
      state.pageViewStopWatch.start();
      commit(TrackingMutation.SetPageViewStopWatchPaused, false);
    } catch (error) {
      return error;
    }
  },
  [TrackingAction.ResetPageViewStopWatch]({ commit, dispatch, state }, pages: DocumentPage[]) {
    if (state.trackingDisabled) {
      return;
    }
    try {
      const pageTime: number = state.pageViewStopWatch.stop();
      commit(TrackingMutation.SetPageViewStopWatchPaused, true);
      if (pages.length > 0) {
        dispatch(TrackingAction.TrackVisitTime, { pages, pageTime });
      }
    } catch (error) {
      console.log(error);
    }
  },
  [TrackingAction.PausePageViewStopWatch]({ commit, state }) {
    if (state.trackingDisabled || state.suspendPageTracking) {
      return;
    }
    try {
      if (state.currentDocumentPages && !state.pageViewStopWatchPaused) {
        state.pageViewStopWatch.pause();
        commit(TrackingMutation.SetPageViewStopWatchPaused, true);
      }
    } catch (error) {
      console.log(error);
    }
  },
  [TrackingAction.ResumePageViewStopWatch]({ commit, state }) {
    if (state.trackingDisabled || state.suspendPageTracking) {
      return;
    }
    try {
      if (state.currentDocumentPages && state.pageViewStopWatchPaused) {
        state.pageViewStopWatch.resume();
        commit(TrackingMutation.SetPageViewStopWatchPaused, false);
      }
    } catch (error) {
      console.log(error);
    }
  },
  [TrackingAction.SuspendPageTracking]({ commit, dispatch, state }) {
    if (!state.currentDocumentPages) {
      return;
    }
    state.currentDocumentPages.forEach((page: DocumentPage) => {
      dispatch(TrackingAction.ResetPageViewStopWatch, page);
    });
    commit(TrackingMutation.SetSuspendPageTracking, true);
  },
  [TrackingAction.ResumePageTracking]({ commit, dispatch }) {
    commit(TrackingMutation.SetSuspendPageTracking, false);
    dispatch(TrackingAction.StartPageViewStopWatch);
  },
  [TrackingAction.EnableTracking]({ commit }) {
    commit(TrackingMutation.SetTrackingDisabled, false);
  },
  [TrackingAction.DisableTracking]({ commit }) {
    commit(TrackingMutation.SetTrackingDisabled, true);
  },
};
